"use client";

import { Trans } from "react-i18next";
import { useClientTranslation } from "@/lib/i18n/client";
import Image from "next/image";
import css from "./Intro.module.css";

export interface IntroProps {
  readonly ownerEmail: string;
  readonly bmcUrl: string;
  readonly className?: string;
  /** "free" codes use the introFree variant, which omits the donation ask. */
  readonly isFree?: boolean;
}

/**
 * Renders the photo-code intro body with two inline links (email + BMC).
 * Lives in i18n as a single sentence with `<email>` and `<bmc>` placeholders
 * that map to the actual anchors here — the translator owns wording, the
 * component owns behaviour. Free codes use `introFree`, which drops the
 * Buy-Me-a-Coffee donation sentence (and so never emits the `<bmc>` link).
 */
export function Intro({ ownerEmail, bmcUrl, className, isFree }: IntroProps) {
  const { t } = useClientTranslation();
  return (
    <p className={className}>
      <Image
        src="/assets/bmc_qr.png"
        alt="Buy me a coffee QR"
        width={128}
        height={128}
        className={css.qr}
      />
      <Trans
        t={t}
        i18nKey={isFree ? "common:photoCode.introFree" : "common:photoCode.intro"}
        components={{
          email: (
            <a
              href={`mailto:${ownerEmail}`}
              aria-label={`Email ${ownerEmail}`}
            />
          ),
          bmc: <a href={bmcUrl} target="_blank" rel="noopener noreferrer" />,
        }}
      />
    </p>
  );
}
